import React, { useState, useEffect } from 'react';
import { InfoTooltip, LoadingState, ErrorState, EmptyState } from './shared/UIComponents';
import { multiOmicsAPI, pathwayAPI, targetAPI} from '../api';

const pathwayInterpretationCopy = {
  general: {
    title: 'General pathway confidence',
    points: [
      'High confidence: low p_value and low adjusted_p_value/FDR, plus multiple matching genes.',
      'Medium confidence: one strong significance signal but limited matching evidence.',
      'Low confidence: borderline significance or very small overlap; treat as exploratory.',
    ],
  },
  senescence: {
    title: 'Senescence pathway confidence',
    points: [
      'matching_genes show overlap with senescence references; larger overlap increases trust.',
      'Use senescence results to prioritize follow-up when overlap is consistent across sources.',
      'If overlap is sparse, treat findings as hypothesis-generating rather than confirmatory.',
    ],
  },
  unified: {
    title: 'Unified pathway confidence and consensus',
    points: [
      'High confidence: higher combined_pathway_score with consensus_count >= 2 and multiple supporting_sources.',
      'Medium confidence: strong score from one source but limited cross-source agreement.',
      'Low confidence: low score or weak agreement; keep for context, not decision-making.',
      'Check detailed_evidence for source-level traceability before final interpretation.',
    ],
  },
};

const PathwayInterpretation = ({ pathwayType }) => {
  const copy = pathwayInterpretationCopy[pathwayType] || pathwayInterpretationCopy.general;
  return (
    <div style={{ marginBottom: 12, border: '1px solid #e6ecf5', backgroundColor: '#f7faff', borderRadius: 8, padding: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>{copy.title}</div>
      <ul style={{ margin: 0, paddingLeft: 20 }}>
        {copy.points.map((point) => (
          <li key={point} style={{ marginBottom: 4 }}>{point}</li>
        ))}
      </ul>
    </div>
  );
};

const getUnifiedConsensusSummary = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const highConsensus = rows.filter((r) => Number(r.consensus_count || 0) >= 2).length;
  const avgScore = rows.reduce((acc, r) => acc + Number(r.combined_pathway_score || 0), 0) / rows.length;
  if (highConsensus === 0) {
    return `Low cross-source agreement (${rows.length} pathways, avg score ${avgScore.toFixed(2)}). Treat as exploratory signal.`;
  }
  return `${highConsensus}/${rows.length} pathways show cross-source agreement (avg score ${avgScore.toFixed(2)}). Confidence is stronger for consensus-supported pathways.`;
};

const Section = ({ title, tooltip, children }) => (
  <div style={{ marginBottom: 24 }}>
    <InfoTooltip title={title} content={tooltip}>
      <h2 style={{ marginBottom: 8 }}>{title}</h2>
    </InfoTooltip>
    {children}
  </div>
);

const Selector = ({ datasets, value, onChange, label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <label style={{ fontWeight: 600 }}>{label}</label>
    <select value={value || ''} onChange={e => onChange(e.target.value)}>
      {(datasets || []).map(ds => (
        <option key={ds} value={ds}>{ds}</option>
      ))}
    </select>
  </div>
);

const SpecificityBadges = ({ badges = [] }) => {
  const normalized = Array.isArray(badges) ? badges : [];
  const classFor = (badge) => {
    switch (badge) {
      case "AD-Enriched":
        return "specificity-badge--ad-enriched";
      case "Aging Associated":
        return "specificity-badge--aging-associated";
      case "General Inflammation Risk":
        return "specificity-badge--inflammation-risk";
      case "Cross-Neurodegeneration Shared":
        return "specificity-badge--cross-neurodegeneration";
      default:
        return "status-badge";
    }
  };

  if (!normalized.length) return <span style={{ color: 'var(--text-muted)' }}>None</span>;

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {normalized.map((b) => (
        <span key={b} className={`specificity-badge ${classFor(b)}`}>
          {b}
        </span>
      ))}
    </div>
  );
};

const Table = ({ headers, rows, emptyMessage }) => {
  if (!rows || rows.length === 0) {
    return (
      <EmptyState
        title="No rows to display"
        description={emptyMessage || 'No data available for the current selection.'}
        icon="📭"
      />
    );
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="table" style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {headers.map(h => (
              <th key={h} style={{ textAlign: 'left', borderBottom: '1px solid var(--border)', padding: '8px 6px' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx}>
              {headers.map(h => (
                <td key={h} style={{ borderBottom: '1px solid var(--border)', padding: '8px 6px', verticalAlign: 'top' }}>
                {h === 'specificity_badges' && Array.isArray(row[h]) && row[h].every((x) => typeof x === 'string') ? (
                  <SpecificityBadges badges={row[h]} />
                ) : Array.isArray(row[h]) ? (
                  row[h].every(item => typeof item !== 'object') ? (
                    row[h].join(', ')
                  ) : (
                    <details>
                      <summary style={{ cursor: 'pointer', color: '#0056b3' }}>
                        View Details ({row[h].length})
                      </summary>

                      <pre
                        style={{
                          marginTop: 8,
                          whiteSpace: 'pre-wrap',
                          fontSize: 12,
                          background: '#f6f8fa',
                          padding: 8,
                          borderRadius: 6,
                          maxWidth: 500,
                          overflowX: 'auto',
                        }}
                      >
                        {JSON.stringify(row[h], null, 2)}
                      </pre>
                    </details>
                  )
                ) : typeof row[h] === 'object' && row[h] !== null ? (
                  <details>
                    <summary style={{ cursor: 'pointer', color: '#0056b3' }}>
                      View Object
                    </summary>

                    <pre
                      style={{
                        marginTop: 8,
                        whiteSpace: 'pre-wrap',
                        fontSize: 12,
                        background: '#f6f8fa',
                        padding: 8,
                        borderRadius: 6,
                        maxWidth: 500,
                        overflowX: 'auto',
                      }}
                    >
                      {JSON.stringify(row[h], null, 2)}
                    </pre>
                  </details>
                ) : (
                  row[h] ?? ''
                )}
              </td>
                
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

function MultiOmicsInsights({ datasets = [], defaultDataset = null }) {
  const [datasetA, setDatasetA] = useState(defaultDataset || datasets[0] || null);
  const [datasetB, setDatasetB] = useState(datasets[1] || datasets[0] || null);

  const [loadingOverlap, setLoadingOverlap] = useState(false);
  const [overlapError, setOverlapError] = useState(null);
  const [overlapSummary, setOverlapSummary] = useState(null);

  const [loadingCorr, setLoadingCorr] = useState(false);
  const [corrError, setCorrError] = useState(null);
  const [corrMatrix, setCorrMatrix] = useState(null);

  const [genesInput, setGenesInput] = useState('');
  const [topN, setTopN] = useState(5);
  const [loadingPathway, setLoadingPathway] = useState(false);
  const [pathwayError, setPathwayError] = useState(null);
  const [pathwayData, setPathwayData] = useState(null);
  const [pathwayType, setPathwayType] = useState('general'); // 'general', 'senescence', or 'unified'

  // Open Targets State
  const [targetGenesInput, setTargetGenesInput] = useState('');
  const [topTargetsN, setTopTargetsN] = useState(10);
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [targetError, setTargetError] = useState(null);
  const [targetData, setTargetData] = useState(null);

  // Keep selectors in sync if datasets prop changes
  useEffect(() => {
    if (datasets && datasets.length > 0) {
      if (!datasetA) setDatasetA(datasets[0]);
      if (!datasetB) setDatasetB(datasets[1] || datasets[0]);
    }
  }, [datasets, datasetA, datasetB]);

  // When defaultDataset changes externally (e.g., user selects in sidebar)
  useEffect(() => {
    if (defaultDataset) setDatasetA(defaultDataset);
  }, [defaultDataset]);

  const fetchOverlap = async (a, b) => {
    if (!a || !b) return;
    setLoadingOverlap(true);
    setOverlapError(null);
    try {
      const data = await multiOmicsAPI.analyzeOverlap(a, b);
      setOverlapSummary(data);
    } catch (e) {
      const message = (e && e.message) ? e.message : 'Could not load overlap summary. Check dataset selection and retry.';
      setOverlapError(message);
      setOverlapSummary(null);
    } finally {
      setLoadingOverlap(false);
    }
  };

  const fetchCorrelations = async (a, b) => {
    if (!a || !b) return;
    setLoadingCorr(true);
    setCorrError(null);
    try {
      const data = await multiOmicsAPI.analyzeMultiOmicsCorrelations(a, b);
      setCorrMatrix(data);
    } catch (e) {
      const message = (e && e.message) ? e.message : 'Could not load cross-dataset correlation. Retry after confirming both datasets are accessible.';
      setCorrError(message);
      setCorrMatrix(null);
    } finally {
      setLoadingCorr(false);
    }
  };

  const handleFetchPathways = async () => {
    // Start with overlapping genes
    let genesToUse = []
    
    if (genesInput && genesInput.length > 0) {
      genesToUse = genesInput.split(/[\s,]+/).map(g => g.trim()).filter(Boolean);
    } else {
      genesToUse = overlapSummary?.overlapping_genes || [];
    }
    // If no overlapping genes, parse the manual text box
    if (!genesToUse.length) {
      setPathwayError('No overlapping genes found between the selected datasets. Please enter a list of genes to perform pathway enrichment.');
      setLoadingPathway(false);
      return;
    }

    // Remove non-gene features
    genesToUse = genesToUse.filter(g => {
      // Keep it if it doesn't have spaces and doesn't start with "Is_", and remove target
      return !g.includes(' ') && !g.toLowerCase().startsWith('is_') && g.toLowerCase() !== 'target' ;
    });

    setLoadingPathway(true);
    setPathwayError(null);
    setPathwayData(null);

    console.log("PAYLOAD TO BACKEND:", JSON.stringify({ genes: genesToUse, top_n: topN }));

    try {
      // Make the API call with the correct list
      if (pathwayType === 'senescence') {
        const data = await pathwayAPI.senescencePathways(genesToUse, topN);
        // Look for matching_genes first, then pathways, then fallback to data
        setPathwayData(data.matching_genes || data.pathways || data || []);
      } else if (pathwayType === 'unified') {
        const data = await pathwayAPI.unifiedEnrichPathways(genesToUse, topN);
        setPathwayData(data.pathways || data || []);
      }
      else {
        const data = await pathwayAPI.enrichPathways(genesToUse, topN);
        setPathwayData(data.pathways || []);
      }
    } catch (error) {
      console.error('Error occurred while fetching pathway enrichment:', error);
      setPathwayError(error?.message || 'Pathway enrichment failed. Check gene format and try again.');
    } finally {
      setLoadingPathway(false);
    }
  };

  const handleFetchTargets = async () => {
    let genesToUse = [];
    if (targetGenesInput && targetGenesInput.length > 0) {
      genesToUse = targetGenesInput.split(/[\s,]+/).map(g => g.trim()).filter(Boolean);
    } else {
      genesToUse = overlapSummary?.overlapping_genes || [];
    }

    if (!genesToUse.length) {
      setTargetError('No overlapping genes found. Please enter a list of genes.');
      setLoadingTargets(false);
      return;
    }

    genesToUse = genesToUse.filter(g => !g.includes(' ') && !g.toLowerCase().startsWith('is_') && g.toLowerCase() !== 'target');
    genesToUse = genesToUse.slice(0, topTargetsN);

    setLoadingTargets(true);
    setTargetError(null);
    setTargetData(null);

    try {
      const response = await targetAPI.getTargetAnnotations(genesToUse);
      // Map JSON to a flat structure for the generic Table component
      const formattedData = (response.annotations || []).map(row => {
        const checkModality = (modalityCode) => {
          if (!Array.isArray(row.tractability)) return '✗ No';
          return row.tractability.some(t => t.modality === modalityCode && t.value === true) ? 'Yes' : 'No';
        };
        const adScore = row.alzheimers_association?.score;
        return {
          'Gene Symbol': row.symbol,
          'Ensembl ID': row.ensembl_id || 'N/A',
          'Small Molecule': checkModality('SM'),
          'Antibody': checkModality('AB'),
          'PROTAC': checkModality('PR'),
          'AD Score': adScore !== undefined && adScore !== null ? adScore.toFixed(4) : 'No data'
        };
      });
      setTargetData(formattedData);
    } catch (error) {
      setTargetError(error?.message || 'Failed to fetch Open Targets annotations.');
    } finally {
      setLoadingTargets(false);
    }
  };

  // Trigger loads when either dataset changes
  useEffect(() => {
    fetchOverlap(datasetA, datasetB);
    fetchCorrelations(datasetA, datasetB);
  }, [datasetA, datasetB]);

  return (
    <div>
      <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <Selector datasets={datasets} value={datasetA || ''} onChange={setDatasetA} label="Dataset A" />
        <Selector datasets={datasets} value={datasetB || ''} onChange={setDatasetB} label="Dataset B" />
      </div>

      <Section 
        title="Overlap Summary" 
        tooltip="Summarizes overlapping and unique genes between the two datasets."
      >
        {loadingOverlap && <LoadingState text="Loading overlap summary..." />}
        {overlapError && <ErrorState message={overlapError} onRetry={() => fetchOverlap(datasetA, datasetB)} />}
        {!loadingOverlap && !overlapError && (
          overlapSummary ? (
            <>
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 16 }}>
                {[
                  ["Dataset A", overlapSummary?.dataset1_name],
                  ["Dataset B", overlapSummary?.dataset2_name],
                  ["A shape", overlapSummary?.dataset1_shape?.join(' × ')],
                  ["B shape", overlapSummary?.dataset2_shape?.join(' × ')],
                  ["Genes in A", overlapSummary?.num_genes_dataset1],
                  ["Genes in B", overlapSummary?.num_genes_dataset2],
                  ["Overlapping genes", overlapSummary?.num_overlapping_genes],
                  ["Unique to A", overlapSummary?.num_unique_to_dataset1],
                  ["Unique to B", overlapSummary?.num_unique_to_dataset2],
                ].map(([k, v]) => (
                  <div key={k} style={{ padding: 12, border: '1px solid var(--border)', borderRadius: 8, minWidth: 180 }}>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{k}</div>
                    <div style={{ fontSize: 20, fontWeight: 700 }}>{v ?? '—'}</div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
                <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>Overlapping genes (top 20)</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {(overlapSummary?.overlapping_genes || []).slice(0, 20).map(g => (
                      <span key={g} style={{ border: '1px solid var(--border)', borderRadius: 12, padding: '2px 8px', fontSize: 12 }}>{g}</span>
                    ))}
                  </div>
                </div>
                <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>Unique to A (top 20)</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {(overlapSummary?.unique_to_dataset1 || []).slice(0, 20).map(g => (
                      <span key={g} style={{ border: '1px solid var(--border)', borderRadius: 12, padding: '2px 8px', fontSize: 12 }}>{g}</span>
                    ))}
                  </div>
                </div>
                <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>Unique to B (top 20)</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {(overlapSummary?.unique_to_dataset2 || []).slice(0, 20).map(g => (
                      <span key={g} style={{ border: '1px solid var(--border)', borderRadius: 12, padding: '2px 8px', fontSize: 12 }}>{g}</span>
                    ))}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <EmptyState
              title="No overlap summary yet"
              description="Select two datasets with shared genes, or upload another dataset to continue."
              icon="🧬"
            />
          )
        )}
      </Section>

      <Section 
        title="Cross-Dataset Correlation (Gene-level)" 
        tooltip="Pearson and Spearman correlations for overlapping genes across the two datasets."
      >
        {loadingCorr && <LoadingState text="Loading correlation results..." />}
        {corrError && <ErrorState message={corrError} onRetry={() => fetchCorrelations(datasetA, datasetB)} />}
        {!loadingCorr && !corrError && (
          corrMatrix ? (
            (() => {
              // Expecting shape from service.analyze_cross_dataset_correlation
              // { dataset1_name, dataset2_name, num_overlapping_genes, num_common_samples, correlation_results: [...] }
              const results = Array.isArray(corrMatrix?.correlation_results) ? corrMatrix.correlation_results : [];
              if (!results.length) {
                return (
                  <EmptyState
                    title="No cross-dataset correlation available"
                    description="No overlapping gene-level correlations were returned for this dataset pair."
                    icon="📉"
                  />
                );
              }

              const sorted = [...results].sort((a, b) => (Math.abs(b?.pearson_correlation ?? 0) - Math.abs(a?.pearson_correlation ?? 0)));
              const headers = [
                'gene',
                'pearson_correlation',
                'pearson_p_value',
                'spearman_correlation',
                'spearman_p_value',
              ];
              const rows = sorted.map(r => ({
                gene: r.gene,
                pearson_correlation: typeof r.pearson_correlation === 'number' ? r.pearson_correlation.toFixed(4) : r.pearson_correlation,
                pearson_p_value: typeof r.pearson_p_value === 'number' ? r.pearson_p_value.toExponential(2) : r.pearson_p_value,
                spearman_correlation: typeof r.spearman_correlation === 'number' ? r.spearman_correlation.toFixed(4) : r.spearman_correlation,
                spearman_p_value: typeof r.spearman_p_value === 'number' ? r.spearman_p_value.toExponential(2) : r.spearman_p_value,
              }));

              return (
                <>
                  <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 12 }}>
                    <div style={{ padding: 12, border: '1px solid var(--border)', borderRadius: 8, minWidth: 180 }}>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Common samples</div>
                      <div style={{ fontSize: 20, fontWeight: 700 }}>{corrMatrix?.num_common_samples ?? '—'}</div>
                    </div>
                    <div style={{ padding: 12, border: '1px solid var(--border)', borderRadius: 8, minWidth: 180 }}>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Overlapping genes</div>
                      <div style={{ fontSize: 20, fontWeight: 700 }}>{corrMatrix?.num_overlapping_genes ?? '—'}</div>
                    </div>
                  </div>

                  <Table headers={headers} rows={rows} emptyMessage="No correlation rows were returned for this dataset pair." />
                </>
              );
            })()
          ) : (
            <EmptyState
              title="No correlation output"
              description="Run overlap on a pair with shared genes, then retry correlation."
              icon="📉"
            />
          )
        )}
      </Section>

      {/* Pathway Enrichment Section */}
      <Section 
        title="Gene Set Enrichment" 
        tooltip="Find general or senescence-specific enriched pathways for a manually specified set of genes."
      >
        <div style={{ marginBottom: 20 }}>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>
              Gene List (comma or space separated) - defaults to overlapping genes if left blank
            </label>
            <textarea 
              value={genesInput}
              onChange={(e) => setGenesInput(e.target.value)}
              placeholder="e.g., APP, APOE, PSEN1, PSEN2"
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid var(--border)', minHeight: '80px', fontFamily: 'inherit' }}
            />
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {/* Type Selector Dropdown */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={{ fontWeight: 600 }}>Type:</label>
              <select 
                value={pathwayType} 
                onChange={(e) => setPathwayType(e.target.value)}
                style={{ padding: '6px', borderRadius: '4px', border: '1px solid var(--border)', backgroundColor: 'white' }}
              >
                <option value="general">General Pathways</option>
                <option value="senescence">Senescence Pathways</option>
                <option value="unified">Unified Pathways</option>
              </select>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={{ fontWeight: 600 }}>Top N:</label>
              <input 
                type="number" 
                value={topN} 
                onChange={(e) => setTopN(Number(e.target.value))}
                min={1}
                style={{ padding: '6px', borderRadius: '4px', border: '1px solid var(--border)', width: '70px' }}
              />
            </div>
            
            <button 
              onClick={handleFetchPathways}
              disabled={loadingPathway}
              style={{ 
                padding: '8px 16px', 
                borderRadius: '4px', 
                backgroundColor: loadingPathway ? 'var(--text-muted)' : 'var(--primary, #0056b3)', 
                color: 'white', 
                border: 'none', 
                cursor: loadingPathway ? 'not-allowed' : 'pointer',
                fontWeight: 600
              }}
            >
              {loadingPathway ? 'Running...' : 'Run Enrichment'}
            </button>
          </div>
        </div>

        {/* Pathway Results Display */}
        {loadingPathway && <LoadingState text={`Fetching ${pathwayType} enrichment...`} />}
        {pathwayError && <ErrorState message={pathwayError} onRetry={handleFetchPathways} />}
        {!loadingPathway && !pathwayError && pathwayData && (
          <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 16, backgroundColor: 'var(--bg-light, #fafafa)' }}>
            <PathwayInterpretation pathwayType={pathwayType} />
            <h3 style={{ marginTop: 0, marginBottom: 12 }}>
              {pathwayType === 'senescence' ? 'Senescence' : pathwayType === 'unified' ? 'Unified' : 'General'} Enrichment Results
            </h3>
            
            {Array.isArray(pathwayData) ? (
              pathwayData.length > 0 ? (
                // If it's an array of objects, uses the Table component
                // If it's just strings, it lists them.
                typeof pathwayData[0] === 'object' ? (
                  <>
                    {pathwayType === 'unified' && (
                      <div style={{ marginBottom: 10, fontSize: 13, color: 'var(--text-muted)' }}>
                        {getUnifiedConsensusSummary(pathwayData)}
                      </div>
                    )}
                    <Table
                      headers={Object.keys(pathwayData[0])}
                      rows={pathwayData}
                      emptyMessage="No pathways matched this gene set and enrichment type."
                    />
                  </>
                ) : (
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {pathwayData.map((p, i) => <li key={i}>{p}</li>)}
                  </ul>
                )
              ) : (
                <EmptyState
                  title="No enriched pathways"
                  description="Try increasing Top N, using a larger gene list, or selecting another pathway type."
                  icon="🧭"
                />
              )
            ) : (
              // Fallback for an unknown JSON structure
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {JSON.stringify(pathwayData, null, 2)}
              </pre>
            )}
          </div>
        )}
      </Section>

      <Section 
        title="Clinical Viability (Open Targets)" 
        tooltip="Check drug tractability and AD association for a user-inputted list of genes."
      >
        <div style={{ marginBottom: 20 }}>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>
              Target Gene List (comma or space separated) - defaults to overlapping genes if left blank
            </label>
            <textarea 
              value={targetGenesInput}
              onChange={(e) => setTargetGenesInput(e.target.value)}
              placeholder="e.g., APOE, TREM2, APP"
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid var(--border)', minHeight: '80px', fontFamily: 'inherit' }}
            />
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={{ fontWeight: 600 }}>Top N to Check:</label>
              <input 
                type="number" 
                value={topTargetsN} 
                onChange={(e) => setTopTargetsN(Number(e.target.value))}
                min={1}
                max={50}
                style={{ padding: '6px', borderRadius: '4px', border: '1px solid var(--border)', width: '70px' }}
              />
            </div>
            
            <button 
              onClick={handleFetchTargets}
              disabled={loadingTargets}
              style={{ 
                padding: '8px 16px', 
                borderRadius: '4px', 
                backgroundColor: loadingTargets ? 'var(--text-muted)' : 'var(--primary, #0056b3)', 
                color: 'white', 
                border: 'none', 
                cursor: loadingTargets ? 'not-allowed' : 'pointer',
                fontWeight: 600
              }}
            >
              {loadingTargets ? 'Checking...' : 'Check Drug Tractability'}
            </button>
          </div>
        </div>

        {loadingTargets && <LoadingState text="Querying Open Targets..." />}
        {targetError && <ErrorState message={targetError} onRetry={handleFetchTargets} />}
        {!loadingTargets && !targetError && targetData && (
          <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 16, backgroundColor: 'var(--bg-light, #fafafa)' }}>
            <div style={{ marginBottom: 12, border: '1px solid #e6ecf5', backgroundColor: '#f7faff', borderRadius: 8, padding: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>How to read this table</div>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li style={{ marginBottom: 4 }}><strong>Tractability:</strong> Indicates if a pharmaceutical company can physically build a drug for this target.</li>
                <li style={{ marginBottom: 4 }}><strong>AD Score:</strong> A score from 0 to 1 showing the strength of genetic/clinical evidence linking this target to Alzheimer's Disease.</li>
              </ul>
            </div>
            <h3 style={{ marginTop: 0, marginBottom: 12 }}>Target Viability Results</h3>
            <Table
              headers={Object.keys(targetData[0] || {})}
              rows={targetData}
              emptyMessage="No clinical data found for these genes."
            />
          </div>
        )}
      </Section>
    </div>
  );
}

export default MultiOmicsInsights;